import { View, Text, StyleSheet } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { mesaColorNames, spacing, typography } from '@/src/common/theme'
import { useNavigation } from 'expo-router'
import { routerBack } from '@/src/common/utils/routerBack'
import { useEffect } from 'react'
import { Section } from '@/src/common/components/Section'
import { z } from 'zod/v4'
import { ID } from '@acre-rewards/lib'
import { withFreeze } from '@/src/experiences/virtual-card/withFreeze'
import { LegacyFreezeParams } from '@/src/experiences/virtual-card/withFreeze/LegacyFreeze'
import {
  useCombinedCardStatusStore,
  FrozenCardStatus,
  LoadedCardStatus,
} from '@/src/experiences/virtual-card/store/useCombinedCardStatusStore'
import { Accordion } from '@/src/design-system/accordion'

const FreezeParams = z.union([
  z.object({ /** for new platform architecture */ cardAccountId: ID.schema.CardAccount }),
  LegacyFreezeParams,
])
export type FreezeParams = z.infer<typeof FreezeParams>

export default withFreeze(function Freeze({ toggleFreeze, freezing, unfreezing }) {
  const { status } = useCombinedCardStatusStore()
  const isCardFrozen = FrozenCardStatus.safeParse(status).success
  const modalText = isCardFrozen ? 'Unfreeze card' : 'Freeze card'
  const navigation = useNavigation()
  const loading = freezing || unfreezing || !LoadedCardStatus.safeParse(status).success

  useEffect(() => {
    navigation.setOptions({
      headerBackVisible: true,
      header: loading
        ? () => <></>
        : () => (
            <Section
              title={modalText}
              style={{ backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'] }}
              headerStyle={{
                height: 'auto',
                marginBottom: 0,
                padding: 24,
              }}
              titleStyles={{ color: mesaColorNames['global/grey/80 #2F3133'] }}
              onClosePress={() => void routerBack()}
              chevronColor={mesaColorNames['Greyscale/500 #6E7375']}
            />
          ),
    })
  }, [navigation, modalText, loading])

  return (
    <View style={styles.modalContainer}>
      <Accordion trigger={false} open={!loading}>
        <Text style={styles.modalText}>
          {isCardFrozen
            ? 'Tap the below button to unfreeze your Mesa Card. You can freeze your card at any time by visiting this page again or contacting support.'
            : 'Tap the below button to freeze your Mesa Card. You can unfreeze your card at any time by visiting this page again or contacting support.'}
        </Text>
      </Accordion>
      <Button
        loading={loading}
        onPress={() => void toggleFreeze(!!isCardFrozen)}
        size="lg"
        buttonStyle={{ height: 48 }}
      >
        <Text style={styles.modalButton}>{modalText}</Text>
      </Button>
    </View>
  )
})

const styles = StyleSheet.create({
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xl,
    marginHorizontal: 16,
    marginBottom: 57,
  },
  modalText: {
    color: mesaColorNames['global/grey/80 #2F3133'],
    textAlign: 'center',
    ...typography['paragraph/md'],
  },
  modalButton: typography['label/md-bold'],
})
